import React from 'react';
import { History, Award, ShieldCheck, Users, Zap, Globe, Microscope, Factory, Scissors } from 'lucide-react';
import aboutimg from '../../assets/aboutapperale.webp';

const ApparelAbout = ({ company }) => {
  // Defensive check: If company is undefined/null, fallback to a default string
  const companyName = company?.name || "Our Manufacturing";

  const stats = [
    { 
      icon: <Users size={32} />, 
      value: "3,500+", 
      label: "Master Artisans", 
      sub: "Expert textile workforce" 
    },
    { 
      icon: <Zap size={32} />, 
      value: "500K", 
      label: "Monthly Output", 
      sub: "Garments per month" 
    },
    { 
      icon: <Globe size={32} />, 
      value: "30+", 
      label: "Nations Served", 
      sub: "Global distribution network" 
    }
  ];

  const values = [
    { 
      title: "Craftsmanship", 
      desc: "Every seam is inspected by three levels of quality control.", 
      icon: <Scissors size={20} /> 
    },
    { 
      title: "Sustainability", 
      desc: "90% water recycling in our dyeing facilities.", 
      icon: <ShieldCheck size={20} /> 
    },
    { 
      title: "Innovation", 
      desc: "Automated cutting and AI-driven pattern nesting.", 
      icon: <Microscope size={20} /> 
    },
    { 
      title: "Integrity", 
      desc: "Fair wages and safe environments for 3,500+ staff.", 
      icon: <Users size={20} /> 
    }
  ];

  return (
    <div className="min-h-screen py-20 px-4 sm:px-6 lg:px-8 bg-[#0a0a0c] text-slate-200 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        
        {/* Hero Section */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
          <div className="relative order-2 lg:order-1">
            <div className="relative z-10 rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl">
              <img 
                src={aboutimg}
                alt="Textile Manufacturing" 
                className="w-full h-[500px] object-cover"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/1000x500?text=Manufacturing+Facility'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-transparent to-transparent opacity-60"></div>
            </div>
            
            <div className="absolute -bottom-6  w-64 h-64 bg-emerald-500/10 blur-[80px] -z-"></div>
            <div className="absolute -top-10 -left-32 p-6 bg-[#141417] border border-white/5 rounded-2xl shadow-2xl hidden md:block">
               <Factory className="text-emerald-500 mb-2" size={40} />
               <p className="text-white font-bold text-xl leading-tight">Smart<br/>Facilities</p>
            </div>
          </div>

          <div className="space-y-8 order-1 lg:order-2">
            <div>
              <span className="text-emerald-500 font-bold tracking-[0.3em] text-xs uppercase mb-4 block">Our Heritage</span>
              <h2 className="text-4xl md:text-6xl font-black text-white leading-[1.1] tracking-tight">
                Precision in <span className="text-slate-500 italic">Every Stitch.</span>
              </h2>
            </div>
            
            <p className="text-slate-400 text-lg leading-relaxed border-l-2 border-emerald-500/30 pl-6">
              {companyName} is a versatile apparel import and supply firm dedicated to sourcing the world’s best clothing products. Our expertise spans the entire textile spectrum, including technical sportswear, casual fashion, denim, linen, and traditional celebratory attire. With a commitment to quality materials and seamless logistics, we provide all-encompassing clothing solutions for businesses worldwide.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-6 bg-[#141417] rounded-2xl border border-white/5 group hover:border-emerald-500/50 transition-colors">
                <History className="text-emerald-500 mb-4 transition-transform group-hover:rotate-[-12deg]" size={32} />
                <h4 className="text-white font-bold text-lg mb-1">20+ Years</h4>
                <p className="text-slate-500 text-sm">Industrial Mastery</p>
              </div>
              <div className="p-6 bg-[#141417] rounded-2xl border border-white/5 group hover:border-emerald-500/50 transition-colors">
                <Award className="text-emerald-500 mb-4 transition-transform group-hover:scale-110" size={32} />
                <h4 className="text-white font-bold text-lg mb-1">GOTS Certified</h4>
                <p className="text-slate-500 text-sm">Organic Standards</p>
              </div>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-32">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <h3 className="text-3xl font-black text-white">Our Core <span className="text-emerald-500">Principles</span></h3>
            <div className="h-px flex-grow bg-white/5 mx-8 hidden md:block"></div>
            <p className="text-slate-500 max-w-xs text-sm">Building the future of fashion through ethical technology and artisanal skill.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div key={i} className="p-8 bg-[#141417] border border-white/5 rounded-3xl hover:bg-emerald-500/5 transition-all group">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-emerald-500 mb-6 group-hover:bg-emerald-500 group-hover:text-black transition-colors">
                  {value.icon}
                </div>
                <h4 className="text-white font-bold text-xl mb-3">{value.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid md:grid-cols-3 gap-0 border border-white/5 rounded-[3rem] overflow-hidden bg-[#141417]">
          {stats.map((stat, i) => (
            <div 
              key={i} 
              className={`p-12 text-center group hover:bg-[#1a1a1e] transition-colors ${i !== stats.length - 1 ? 'md:border-r border-white/5' : ''}`}
            >
              <div className="text-emerald-500 flex justify-center mb-6 group-hover:-translate-y-2 transition-transform duration-500">
                {stat.icon}
              </div>
              <div className="text-5xl font-black text-white mb-2 tracking-tighter">{stat.value}</div>
              <div className="text-emerald-400 font-bold uppercase tracking-[0.2em] text-[10px] mb-4">{stat.label}</div>
              <p className="text-slate-500 text-sm">{stat.sub}</p>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default ApparelAbout;